// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Authentication state management with Zustand.
 * Handles user session, tokens, and auth actions.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { logger } from '../lib/logger';
import type { AuthStore, User } from '../types/auth';
import type { StateStorage } from 'zustand/middleware';

/**
 * Create a safe localStorage wrapper that falls back to memory storage
 * if localStorage is unavailable (e.g., private browsing, quota exceeded).
 */
const createSafeStorage = (): StateStorage => {
  try {
    // Test localStorage availability
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return localStorage;
  } catch {
    logger.warn('localStorage_unavailable', {
      fallback: 'memory',
      store: 'authStore',
    });
    // Return no-op storage as fallback
    const memoryStorage: Record<string, string> = {};
    return {
      getItem: (key: string) => memoryStorage[key] ?? null,
      setItem: (key: string, value: string) => {
        memoryStorage[key] = value;
      },
      removeItem: (key: string) => {
        delete memoryStorage[key];
      },
    };
  }
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      error: null,
      showSignedOutModal: false,

      // Actions
      setAuth: (user: User, accessToken: string, refreshToken: string) => {
        logger.info('auth_set', {
          userId: user.user_id,
          username: user.username,
        });
        set({
          user,
          accessToken,
          refreshToken,
          error: null,
        });

        // Theme integration: Save current theme and load user's preference
        import('./themeStore')
          .then(({ useThemeStore }) => {
            const themeStore = useThemeStore.getState();
            themeStore.savePreLoginTheme();
            themeStore.loadUserTheme(user.theme_preference);
          })
          .catch(error => {
            logger.error('theme_integration_error', { error });
          });
      },

      // Update user profile without triggering theme reload (for theme sync)
      updateUserProfile: (updates: Partial<User>) => {
        const current = get().user;
        if (current) {
          set({
            user: { ...current, ...updates },
          });
        }
      },

      clearAuth: () => {
        logger.info('auth_cleared', {});
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          error: null,
        });

        // Theme integration: Restore pre-login theme
        import('./themeStore')
          .then(({ useThemeStore }) => {
            const themeStore = useThemeStore.getState();
            themeStore.restorePreLoginTheme();
          })
          .catch(error => {
            logger.error('theme_restoration_error', { error });
          });
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      setError: (error: string | null) => {
        if (error) {
          logger.error('auth_error', { error });
        }
        set({ error, isLoading: false });
      },

      setShowSignedOutModal: (show: boolean) => {
        set({ showSignedOutModal: show });
      },

      // Selectors
      isAuthenticated: () => {
        const state = get();
        if (!state.accessToken || !state.user) {
          return false;
        }

        // Decode JWT and check expiry
        // Note: We don't clear auth here - App.tsx handles token refresh
        // This prevents mid-game logout when token expires but refresh is pending
        try {
          const payload = JSON.parse(atob(state.accessToken.split('.')[1]));
          const exp = payload.exp * 1000; // Convert to milliseconds
          const now = Date.now();

          if (now >= exp) {
            // Token expired - but don't clear auth here
            // App.tsx will attempt refresh or show proper error
            logger.debug('token_expired_check', { exp, now });
            return false;
          }

          return true;
        } catch (error) {
          logger.error('token_validation_error', { error });
          // Invalid token format - still don't clear here
          // Let the token refresh logic in App.tsx handle it
          return false;
        }
      },

      getAccessToken: () => {
        // Return token regardless of expiry - let caller handle refresh
        // This is needed for the refresh flow in App.tsx
        return get().accessToken;
      },

      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const { api } = await import('../lib/api');
          const response = await api.login({ username, password });

          const user = {
            user_id: response.user_id,
            username: response.username,
            permissions: response.permissions,
            theme_preference: response.theme_preference,
            theme_primary_color: response.theme_primary_color,
            profile_emoji: response.profile_emoji,
            namespace: response.namespace,
          };

          get().setAuth(user, response.access_token, response.refresh_token);
          logger.info('login_success', { username });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          logger.error('login_error', { error: message });
          set({ error: message, isLoading: false });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'undef-auth-storage',
      storage: createJSONStorage(() => createSafeStorage()),
      partialize: state => ({
        // Only persist tokens and user info, not loading/error states
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
);

// Expose on window for E2E testing (dev only — never in production builds)
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { useAuthStore: typeof useAuthStore }).useAuthStore = useAuthStore;
}
